import React, { Component, ContextType } from 'react';

import { ToastsContext } from '@sbu/contexts.toasts';

import Error, { ErrorProps } from 'app/Error';

import { captureError } from './errors';

interface ErrorInfo {
  componentStack: string;
}

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  showMessage?: boolean;
  showNotification?: boolean;
  showChildren?: boolean;
  message?: React.ReactNode;
  onError?: (props: { error: Error; info: ErrorInfo }) => void;
  ErrorProps?: ErrorProps;
}

interface ErrorBoundaryState {
  error?: Error;
  info?: ErrorInfo;
}

// Prevent showing loading failed messages like
// "Loading chunk 10 failed. (error: http.../10.chunk.js)"
const blacklistedErrors = ['Loading chunk', 'react'];

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static defaultProps: ErrorBoundaryProps = {
    showMessage: true,
    showChildren: false,
    showNotification: false,
  };

  static contextType = ToastsContext;
  // @ts-ignore
  context!: ContextType<typeof ToastsContext>;

  state: ErrorBoundaryState = {};

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { onError, showNotification } = this.props;

    if (this.state.error) {
      return;
    }

    // Capture the browser error event.
    captureError(error);

    // Get last event ID for feedback report
    const eventId = window.Sentry ? window.Sentry.lastEventId() : '';

    if (showNotification) {
      const title = this.filterErrorMessage(error);
      this.context.addError(title, {
        linkProps: { onClick: () => this.onErrorClick(eventId) },
        linkText: 'Report feedback',
      });
    }

    this.setState({ error, info });

    if (onError) {
      onError({ error, info });
    }
  }

  filterErrorMessage(error: Error) {
    let cleaned = 'Something went wrong...';

    if (
      error &&
      error.message &&
      blacklistedErrors.filter((f) => error.message.indexOf(f) > -1).length ===
        0
    ) {
      cleaned = error.message;
    }

    return cleaned;
  }

  onErrorClick(eventId: string) {
    // TODO: In future implement our own UI for this
    // https://docs.sentry.io/api/projects/post-project-user-reports/
    if (window.Sentry) {
      Sentry.showReportDialog({
        eventId,
      });
    }
  }

  render() {
    const { children, message, showMessage, showChildren, ErrorProps } =
      this.props;
    const { error } = this.state;
    const showMessageText = error && message && showMessage;
    const shouldShowChildren = (error && showChildren) || !error;

    return (
      <>
        {!showMessageText && !shouldShowChildren ? (
          <Error {...ErrorProps} />
        ) : (
          <>
            {showMessageText && message}
            {shouldShowChildren && children}
          </>
        )}
      </>
    );
  }
}
